import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Errors } from "services/forms/validation/types";
import { validateYupWithFeedback } from "services/forms/validation/feedback";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";

import { getSalesChannelsAPI } from "services/api/sales/channels/api";
import {
  addChannelImageAPI,
  createListingAPI,
  getListingAPI,
  removeChannelImageAPI,
  updateListingAPI,
  updateListingChannelAPI,
  updateMarketplaceDetailsAPI,
} from "services/api/sales/listings/api";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { getFleetVehiclePicturesAPI } from "services/api/fleet/vehicles/pictures/api";
import { getAdditionalEquipmentByJomAPI } from "services/api/catalog/cvh/api";
import { initialPagination } from "ui/components/Table/consts";
import { PaginationDirection } from "services/url";

import { Modal, SuccessOverlay } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Wizard } from "ui/components/Wizard";
import WizardStep from "ui/components/Wizard/WizardStep";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import { Loader } from "ui/components/Loader";
import successAnimation from "ui/theme/animations/success.json";

import ChannelPickerStep from "./components/ChannelPickerStep/ChannelPickerStep";
import MarketplaceStep from "./components/MarketplaceStep/MarketplaceStep";
import AdditionalInfoStep from "./components/AdditionalInfoStep/AdditionalInfoStep";
import PictureStep from "./components/PictureStep/PictureStep";
import PriceStep from "./components/PriceStep/PriceStep";
import PreviewStep from "./components/PreviewStep/PreviewStep";
// Per-kanal override step (ChannelDetailStep) zamijenjen je novim PreviewStep-om
// koji prikazuje live preview po oglasniku + override formu u tabbed layoutu.
// Komponenta ChannelDetailStep i dalje postoji u repu za eventualni revert.
import ListingIntro from "./components/ListingIntro/ListingIntro";

import { LISTING_WIZARD_STEPS, MARKETPLACE_CHANNEL_CODE, initialChannelDataEntry, initialListingEditForm } from "./consts";
import { buildEditFormValues, prefillExtraFromVehicle, prefillSpecsFromVehicle, prefillFromListingDefaults, prefillTiresFromTireSet } from "./mapping/prefill";
import { getFleetTireSetByVehicleActiveAPI } from "services/api/fleet/tires/api";
import { matchCvhEquipment, matchEquipment } from "./mapping/equipmentMatcher";
import { applyListingTemplate } from "./mapping/template";
import { getListingTemplatesAPI } from "services/api/settings/listings/api";
import { getFleetVehicleEquipmentAPI } from "services/api/fleet/vehicles/equipment/api";
import { buildMarketplacePayload } from "./mapping/buildPayload";
import { getListingDefaultsAPI } from "services/api/settings/listings/api";
import { getStepSchema } from "./validation";
import {
  ChannelDataEntry,
  ListingEditCardProps,
  ListingEditFormValues,
  WizardPictureItem,
} from "./types";
import { VehicleListing } from "services/api/sales/listings/types";

const ListingEditCard: React.FC<ListingEditCardProps> = (props) => {
  const { show, fleetVehicleId, fleetVehicleName, listingId, onSuccess, onClose } = props;
  const isEditMode = !!listingId;

  const { createDialog } = useDialog();

  const [formValues, setFormValues] = useState<ListingEditFormValues>(initialListingEditForm);
  const [errors, setErrors] = useState<Errors>({});
  const [stepCurrent, setStepCurrent] = useState(0);
  // isLoading starta `true` da prvi render kad se modal otvori prikaže Loader
  // (bez flash-a praznog Wizarda dok bootstrap useEffect ne stigne fire-at).
  // Reset na `true` se događa i kad se modal zatvori (useEffect ispod) pa je
  // sljedeće otvaranje također spinning od starta.
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const finishedListingIdRef = useRef<string | null>(null);
  // U EDIT modu: postojeći listing iz BE-a. Koristi se u handleFinish za
  // mapiranje SalesChannel.id → VehicleListingChannel.id i za picture diff
  // (image record id-jevi za removeChannelImageAPI).
  const [existingListing, setExistingListing] = useState<VehicleListing | null>(null);

  const marketplaceChannelId = useMemo(
    () => formValues.channels.find((c) => c.code === MARKETPLACE_CHANNEL_CODE)?.id ?? null,
    [formValues.channels]
  );

  const channelDetailSteps = useMemo(
    () =>
      [...formValues.channels]
        .filter((c) => c.id !== marketplaceChannelId && formValues.selectedChannelIds.includes(c.id))
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [formValues.channels, formValues.selectedChannelIds, marketplaceChannelId]
  );

  // U EDIT modu: getListingAPI vraća sve (vehicle + pictures), pa nema potrebe
  // za zasebnim pozivima na fleet endpointe. U CREATE modu listing još ne
  // postoji, pa idemo direktno na fleet endpointe za prefill iz vozila.
  const bootstrapWizard = useCallback(async (vehicleId: string, listingIdArg: string | null) => {
    setIsLoading(true);
    try {
      // EDIT mode — sve dolazi iz listing endpointa.
      if (listingIdArg) {
        const [chs, listingRes, editTemplates] = await Promise.all([
          getSalesChannelsAPI(),
          getListingAPI(listingIdArg).catch((err) => {
            // eslint-disable-next-line no-console
            console.error("[ListingEditCard] getListingAPI failed", err);
            return null;
          }),
          getListingTemplatesAPI().catch(() => []),
        ]);
        const sorted = [...chs].sort((a, b) => a.sortOrder - b.sortOrder);
        setExistingListing(listingRes ?? null);
        if (listingRes) {
          const items: WizardPictureItem[] = (listingRes.fleetVehiclePictures ?? []).map((p) => ({
            id: p.id,
            url: p.url,
            name: p.name,
          }));
          const mpChannelId = sorted.find((c) => c.code === MARKETPLACE_CHANNEL_CODE)?.id ?? null;
          // Predlošci se nude i pri uređivanju — postojeći tekst se ne dira dok
          // korisnik sam ne odabere predložak.
          setFormValues({
            ...buildEditFormValues(listingRes, mpChannelId, sorted, listingRes.vehicle, items),
            templates: editTemplates,
          });
          return;
        }
      }

      // CREATE mode — listing još ne postoji, dohvat fleet vozila + slika
      // ide zasebnim endpointima jer su izvor prefill-a.
      const picturePagination = {
        ...initialPagination,
        pageSize: 100,
        sort: { sortBy: "displayOrder", direction: PaginationDirection.Ascending },
      };
      const [chs, vehicleRes, picturesRes, listingDefaults, activeTireSet, listingTemplates] = await Promise.all([
        getSalesChannelsAPI(),
        getFleetVehicleAPI(vehicleId),
        getFleetVehiclePicturesAPI(vehicleId, picturePagination),
        getListingDefaultsAPI().catch(() => null),
        getFleetTireSetByVehicleActiveAPI(vehicleId).catch(() => null),
        getListingTemplatesAPI().catch(() => []),
      ]);
      const sorted = [...chs].sort((a, b) => a.sortOrder - b.sortOrder);
      setExistingListing(null);

      // Tenant zadane postavke (kontakt/lokacija/plaćanje/jamstvo) — prefill u
      // CREATE modu, korisnik smije override-ati. Tihi catch: bez postavki
      // wizard i dalje radi (prazna polja).
      const defaultsPrefill = listingDefaults ? prefillFromListingDefaults(listingDefaults) : null;

      // CVH equipment fetch — ako vozilo ima JOM, dohvati listu serijske
      // opreme po JOM-u i kroz `matchCvhEquipment` fuzzy-mapiraj na chip
      // codes (`abs`, `esp`, ...). Tihi catch — bez opreme prefill je
      // i dalje OK, user samo mora ručno označiti chip-ove.
      const jom = (vehicleRes as any)?.vehicleType?.jom;
      let equipmentPrefill: Record<string, boolean> = {};
      if (jom) {
        try {
          const cvhEquipment = await getAdditionalEquipmentByJomAPI(jom);
          equipmentPrefill = matchCvhEquipment(cvhEquipment);
        } catch {
          // CVH endpoint mogao 5xx ili JOM nepostojeći — preskoči prefill.
        }
      }

      // Oprema unesena na samom vozilu opisuje baš taj auto, pa dopunjava katalošku listu. Oba
      // izvora postavljaju samo `true`, dakle unija ništa ne odznačava — korisnik i dalje odlučuje.
      // Što se ne poklopi ni s jednim čipom, ide korisniku na uvid u koraku Oprema.
      let unmatchedEquipment: string[] = [];
      try {
        const vehicleEquipment = await getFleetVehicleEquipmentAPI(vehicleId);
        const matched = matchEquipment(vehicleEquipment);

        equipmentPrefill = { ...equipmentPrefill, ...matched.selected };
        unmatchedEquipment = matched.unmatched;
      } catch {
        // Bez opreme vozila prefill ostaje na kataloškom — čarobnjak mora raditi i tada.
      }

      // Mapiranje FleetVehiclePictureRow → WizardPictureItem. `id` se postavlja
      // na `upload.id` jer addChannelImageAPI očekuje uploadId, ne picture id.
      const items: WizardPictureItem[] = (picturesRes.data ?? []).map((row: any) => ({
        id: row.upload?.id ?? row.id,
        url: row.url,
        name: row.upload?.originalFileName ?? row.name,
      }));

      // Predodabiru se samo kanali koje tenant smije koristiti (instalirana integracija).
      const allChannelIds = sorted.filter((c) => c.isAvailable !== false).map((c) => c.id);
      const allPictureIds = items.map((p) => p.id);
      const specsPrefill = prefillSpecsFromVehicle(vehicleRes);
      const extraPrefill = prefillExtraFromVehicle(vehicleRes);
      // Gume iz aktivnog tire seta vozila imaju prioritet nad kataloškim osovinama.
      const tireSetPrefill = prefillTiresFromTireSet(activeTireSet);
      // Inicijalno pre-popuni osnovnu cijenu na tržišnu procjenu (prodajna
      // cijena vozila). Korisnik je može slobodno mijenjati u PriceStep-u.
      const sellingPrice = (vehicleRes as any)?.price?.sellingPrice ?? null;
      const priceString = sellingPrice != null ? String(sellingPrice) : "";

      // Pre-popuni i per-kanal `priceOverride` (samo ne-marketplace kanali) —
      // svaki oglasnik krene s istom procjenom kao baseline. Korisnik može
      // prilagoditi pojedinačno u PriceStep-u ("Vrati" briše override).
      const channelDataPrefill: Record<string, { titleOverride: string; priceOverride: string; descriptionOverride: string }> = {};
      if (priceString) {
        for (const ch of sorted) {
          if (ch.code === MARKETPLACE_CHANNEL_CODE || ch.isAvailable === false) continue;
          channelDataPrefill[ch.id] = {
            titleOverride: "",
            priceOverride: priceString,
            descriptionOverride: "",
          };
        }
      }

      // Zadani predložak popuni naslov i opis odmah; varijable ({marka}, {model}…)
      // zamjenjuju se podacima ovog vozila. Ostali predlošci biraju se u koraku
      // "Podaci oglasa".
      const defaultTemplate = listingTemplates.find((tpl) => tpl.isDefault) ?? null;
      const templatePrefill = defaultTemplate
        ? applyListingTemplate(defaultTemplate, vehicleRes ?? null)
        : null;

      setFormValues((prev) => ({
        ...prev,
        selectedChannelIds: allChannelIds,
        selectedPictureUploadIds: allPictureIds,
        templates: listingTemplates,
        templateId: defaultTemplate?.id ?? "",
        baseTitle: templatePrefill?.baseTitle || prev.baseTitle,
        baseDescription: templatePrefill?.baseDescription || prev.baseDescription,
        basePrice: priceString || prev.basePrice,
        channelData: { ...prev.channelData, ...channelDataPrefill },
        marketplaceData: {
          ...prev.marketplaceData,
          specs: { ...prev.marketplaceData.specs, ...specsPrefill, ...tireSetPrefill },
          extra: { ...prev.marketplaceData.extra, ...(defaultsPrefill?.extra ?? {}), ...extraPrefill },
          equipment: {
            ...prev.marketplaceData.equipment,
            selected: { ...prev.marketplaceData.equipment.selected, ...equipmentPrefill },
          },
          location: { ...prev.marketplaceData.location, ...(defaultsPrefill?.location ?? {}) },
          // Kontakt se prefilla iz postavki da korisnik odmah vidi tko će biti na oglasu.
          contact: { ...prev.marketplaceData.contact, ...(defaultsPrefill?.contact ?? {}) },
          payment: { ...prev.marketplaceData.payment, ...(defaultsPrefill?.payment ?? {}) },
        },
        channels: sorted,
        vehicle: vehicleRes ?? null,
        pictures: items,
        unmatchedEquipment,
      }));
    } catch {
      // greške rukuje createApiCall (snackbar notifikacija)
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Resetira formu i pokreće bootstrap svaki put kad se modal otvori za novo
  // vozilo. Ovisi o `show` zato da se prefill ponovi i ako se modal otvori
  // više puta zaredom.
  useEffect(() => {
    if (!show || !fleetVehicleId) return;
    setFormValues({ ...initialListingEditForm, baseTitle: fleetVehicleName ?? "" });
    setStepCurrent(0);
    setErrors({});
    setExistingListing(null);
    setIsLoading(true);
    bootstrapWizard(fleetVehicleId, listingId ?? null);
  }, [show, fleetVehicleId, fleetVehicleName, listingId, bootstrapWizard]);

  // Kad se modal zatvori, vrati isLoading na `true` tako da sljedeće
  // otvaranje krene odmah u loading state (bez bljeska praznog Wizarda).
  useEffect(() => {
    if (!show) setIsLoading(true);
  }, [show]);

  // Uključuje/isključuje kanal u step 1. Marketplace je obavezan pa se
  // njegov toggle ignorira.
  const toggleChannel = (channelId: string) => {
    if (channelId === marketplaceChannelId) return;
    setFormValues((prev) => {
      const has = prev.selectedChannelIds.includes(channelId);
      return {
        ...prev,
        selectedChannelIds: has
          ? prev.selectedChannelIds.filter((id) => id !== channelId)
          : [...prev.selectedChannelIds, channelId],
      };
    });
  };

  // Sprema parcijalne izmjene per-kanal podataka (override-i + channel-specific
  // polja) u `formValues.channelData` mapu, ključano po `SalesChannel.id`.
  // Koristi se u PriceStep-u za per-kanal `priceOverride` i kasnije u
  // (zakomentiranom) ChannelDetailStep-u za naslov/opis override.
  const handleChannelDataChange = (channelId: string, next: ChannelDataEntry) => {
    setFormValues((prev) => ({
      ...prev,
      channelData: { ...prev.channelData, [channelId]: next },
    }));
  };

  // Sastavlja listu WizardStep elemenata u runtime-u: picker (uvijek),
  // marketplace (uvijek), pa po jedan ChannelDetailStep za svaki ne-marketplace
  // odabrani kanal (sortirano po SalesChannel.sortOrder). Lista se rebuilda
  // svaki put kad se promijeni odabir kanala ili sadržaj forme.
  const steps = useMemo(() => {
    const arr: React.ReactElement[] = [];
    arr.push(
      <WizardStep
        key="picker"
        stepNumber={LISTING_WIZARD_STEPS.channelPicker}
        stepCurrent={stepCurrent}
      >
        <div className="flex items-center justify-center px-4 min-h-[70vh] 2xl:min-h-[1000px]">
          <div className="w-full max-w-4xl">
            <ChannelPickerStep
              channels={formValues.channels}
              selectedChannelIds={formValues.selectedChannelIds}
              marketplaceChannelId={marketplaceChannelId}
              onToggle={toggleChannel}
            />
          </div>
        </div>
      </WizardStep>
    );
    arr.push(
      <WizardStep
        key="marketplace"
        stepNumber={LISTING_WIZARD_STEPS.marketplace}
        stepCurrent={stepCurrent}
      >
        <div className="px-4 py-6">
          <MarketplaceStep
            formValues={formValues}
            setFormValues={setFormValues}
            errors={errors}
          />
        </div>
      </WizardStep>
    );
    arr.push(
      <WizardStep
        key="additionalInfo"
        stepNumber={LISTING_WIZARD_STEPS.additionalInfo}
        stepCurrent={stepCurrent}
      >
        <div className="px-4 py-6">
          <AdditionalInfoStep
            formValues={formValues}
            setFormValues={setFormValues}
            errors={errors}
          />
        </div>
      </WizardStep>
    );
    arr.push(
      <WizardStep
        key="pictures"
        stepNumber={LISTING_WIZARD_STEPS.pictures}
        stepCurrent={stepCurrent}
      >
        <div className="px-4 py-6">
          <PictureStep
            formValues={formValues}
            setFormValues={setFormValues}
            errors={errors}
          />
        </div>
      </WizardStep>
    );
    arr.push(
      <WizardStep
        key="price"
        stepNumber={LISTING_WIZARD_STEPS.price}
        stepCurrent={stepCurrent}
      >
        <div className="px-4 py-6">
          <PriceStep
            formValues={formValues}
            setFormValues={setFormValues}
            errors={errors}
            onChannelDataChange={handleChannelDataChange}
          />
        </div>
      </WizardStep>
    );
    // Završni preview/prilagodba step — jedan WizardStep s tab-bar-om umjesto
    // jednog step-a po kanalu. Marketplace izostavljen (njegov sadržaj se već
    // unosi u prethodnim koracima i koristi kao baseline).
    arr.push(
      <WizardStep
        key="preview"
        stepNumber={LISTING_WIZARD_STEPS.preview}
        stepCurrent={stepCurrent}
      >
        <div className="px-4 py-6">
          <PreviewStep
            formValues={formValues}
            setFormValues={setFormValues}
            errors={errors}
            onChannelDataChange={handleChannelDataChange}
          />
        </div>
      </WizardStep>
    );
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepCurrent, formValues, marketplaceChannelId, errors, channelDetailSteps]);

  // Validira trenutni korak prema shemi iz `getStepSchema` (mapa korak → shema živi u
  // validation.ts uz `LISTING_WIZARD_STEPS`). Koraci bez sheme prolaze — oglas se smije
  // pustiti bez slika, bez dodatnih informacija i bez per-kanal prilagodbi.
  // Forma je duga: `validateYupWithFeedback` uz greške javi i porukom što nedostaje te skoči
  // na prvo takvo polje, jer korisnik s dna koraka inače ne vidi zašto „Dalje" ne radi.
  const validateStep = (step: number): boolean => {
    const schema = getStepSchema(step);

    if (!schema) {
      setErrors({});
      return true;
    }

    return validateYupWithFeedback(formValues, schema, setErrors);
  };

  // Validira current step pa pomiče wizard naprijed. Ako validacija ne prođe,
  // `setErrors` već prikazuje greške i nema napretka.
  const handleNextStep = async () => {
    if (!validateStep(stepCurrent)) return;
    setStepCurrent((s) => s + 1);
  };

  // Vraća wizard korak unazad i čisti errors da prethodni korak ne pokaže
  // stare validacijske poruke.
  const handleBackStep = () => {
    setErrors({});
    setStepCurrent((s) => Math.max(0, s - 1));
  };

  // Submit pipeline (sekvencijalan):
  //   1. Validira zadnji korak.
  //   2. Kreira listing (BE auto-generira inactive kanal po SalesChannel-u).
  //   3. Za svaki ODABRANI kanal aktivira ga i šalje samo title/description
  //      override (cijena i ostali detalji žive na marketplace base-u).
  //   4. Za marketplace dodaje odabrane slike kao channel-images. Per-kanal
  //      slike se ne biraju u wizardu — kanali nasljeđuju marketplace selekciju.
  //   5. Pamti listing.id i prikazuje success overlay; onSuccess se okida tek
  //      kad korisnik zatvori overlay (vidi `handleSuccessDismiss`).
  const handleFinish = async () => {
    if (!fleetVehicleId) return;
    if (!validateStep(stepCurrent)) return;

    setIsLoading(true);
    try {
      // U EDIT modu radimo update na postojećem listingu (POST createListing bi
      // bacio "već postoji" na BE-u). U CREATE modu prvo kreiramo listing pa
      // BE auto-generira inactive kanal po SalesChannel-u.
      const listing = isEditMode && existingListing
        ? await updateListingAPI(existingListing.id, {
            baseTitle: formValues.baseTitle || null,
            basePrice: formValues.basePrice ? Number(formValues.basePrice) : null,
            baseDescription: formValues.baseDescription || null,
          })
        : await createListingAPI({
            fleetVehicleId,
            baseTitle: formValues.baseTitle || null,
            basePrice: formValues.basePrice ? Number(formValues.basePrice) : null,
            baseDescription: formValues.baseDescription || null,
          });

      // Sprema sve podatke iz marketplace step-a (specs, oprema, plaćanje,
      // lokacija, kontakt, video) na novom dedicated endpointu.
      await updateMarketplaceDetailsAPI(listing.id, buildMarketplacePayload(formValues));

      for (const ch of listing.channels) {
        const isSelected = formValues.selectedChannelIds.includes(ch.salesChannelId);
        const data = formValues.channelData[ch.salesChannelId] ?? initialChannelDataEntry;

        // U EDIT modu obrađujemo SVE kanale — odselektirani se deaktiviraju
        // (isActive=false). U CREATE modu se odselektirani preskaču (defaultno
        // su već inactive na BE-u).
        if (!isSelected && !isEditMode) continue;

        await updateListingChannelAPI(listing.id, ch.id, {
          isActive: isSelected,
          titleOverride: data.titleOverride || null,
          priceOverride: data.priceOverride ? Number(data.priceOverride) : null,
          descriptionOverride: data.descriptionOverride || null,
        });

        if (ch.salesChannelCode === MARKETPLACE_CHANNEL_CODE) {
          if (isEditMode) {
            // Picture diff: match po uploadId, jer wizard barata uploadId-jevima
            // a removeChannelImageAPI traži image record id.
            const currentImages = ch.images ?? [];
            const selectedSet = new Set(formValues.selectedPictureUploadIds);
            const currentSet = new Set(currentImages.map((img) => img.uploadId));

            // Skini one koje više nisu u selekciji.
            for (const img of currentImages) {
              if (!selectedSet.has(img.uploadId)) {
                await removeChannelImageAPI(listing.id, ch.id, img.id);
              }
            }
            // Dodaj nove + preindex sortOrder po trenutnom redoslijedu u formi.
            // Postojeće slike koje su ostale: ažuriramo im sortOrder kroz
            // re-add nije moguć (jedinstvenost po uploadId); zasada zadržavamo
            // BE sortOrder za postojeće, a nove dodajemo na kraj. Ako bude
            // potrebno full reorder — proširiti BE endpointom za reorder.
            for (let i = 0; i < formValues.selectedPictureUploadIds.length; i++) {
              const uploadId = formValues.selectedPictureUploadIds[i];
              if (!currentSet.has(uploadId)) {
                await addChannelImageAPI(listing.id, ch.id, { uploadId, sortOrder: i });
              }
            }
          } else {
            for (let i = 0; i < formValues.selectedPictureUploadIds.length; i++) {
              await addChannelImageAPI(listing.id, ch.id, {
                uploadId: formValues.selectedPictureUploadIds[i],
                sortOrder: i,
              });
            }
          }
        }
      }

      finishedListingIdRef.current = listing.id;
      setShowSuccess(true);
    } catch {
      // handled by createApiCall
    }
    setIsLoading(false);
  };

  // Zatvara success overlay i okida `onSuccess(listingId)` u parent komponenti
  // (page tada navigira na ListingDetailPage).
  const handleSuccessDismiss = () => {
    const id = finishedListingIdRef.current;
    finishedListingIdRef.current = null;
    setShowSuccess(false);
    if (id) onSuccess(id);
  };

  // Otvara confirm dialog prije zatvaranja modala da korisnik ne izgubi unos
  // slučajno (save-na-kraju → mid-wizard close = svi podaci se gube).
  const handleClose = () => {
    createDialog({ onConfirm: onClose });
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="full"
        position="side"
        intro={{
          content: (
            <ListingIntro
              formValues={formValues}
              stepCurrent={stepCurrent}
              stepCount={steps.length}
              isEdit={isEditMode}
            />
          ),
        }}
      >
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title={isEditMode ? "Uredi oglas" : "Unos novog oglasa"}
          onHide={handleClose}
        />
        <ModalBody>
          {isLoading ? (
            <div className="relative flex items-center justify-center px-4 h-[calc(100vh-150px)]">
              <Loader isLoading={true}> </Loader>
            </div>
          ) : (
            <Wizard steps={steps} />
          )}
        </ModalBody>
        <WizardFooter
          stepCurrent={stepCurrent}
          steps={steps}
          onNextStep={handleNextStep}
          onBackStep={handleBackStep}
          onFinish={handleFinish}
          isLoading={isLoading}
          isReadOnly={false}
        />
      </Modal>
      <SuccessOverlay
        show={showSuccess}
        animation={successAnimation}
        title={isEditMode ? "Oglas je uspješno ažuriran!" : "Oglas je uspješno kreiran!"}
        description="Možete ga pregledati i nastaviti uređivanje na stranici oglasa."
        onDismiss={handleSuccessDismiss}
      />
    </>
  );
};

export default memo(ListingEditCard);
